// How this engine works.
//
// It owns the "fetch results for the active trigger" lifecycle. Each time the query
// changes, `run(query, providers)`:
//   1. asks every provider for a flat array, grouped snapshot, or promise of either,
//   2. merges all providers' rows into one list (preserving section order), and
//   3. emits one discriminated state via `onStateChange` — `loading`, `done`, `empty`,
//      or `error`.
//
// The user keeps typing while async fetches are in flight, so multiple runs overlap.
// Three mechanisms keep that correct and flicker-free:
//
// `generation` — the correctness guard.
//   An integer bumped on every `run` (and `cancel`): it marks which keystroke's query is
//   the current truth.
//       run("we")   → generation = 1, kicks off async fetch A
//       run("wea")  → generation = 2, kicks off async fetch B
//   An async fetch calls `settle(generation, …)` with the generation it started under, and
//   `settle` drops the result if a newer run has begun:
//       if (generation !== this.generation) return; // superseded by a newer query
//   So if fetch A (generation 1) resolves after the user typed "wea" (generation 2), A's
//   results are discarded as stale. The displayed results always match the latest query,
//   regardless of resolution order, and correctness depends only on this counter — not on
//   fetches actually stopping.
//
// `AbortController` — a courtesy, not the safety net.
//   Each run creates a fresh controller and passes its signal to every `getResults`; the
//   next run (or `cancel`) aborts the previous one. This lets a well-behaved provider
//   cancel a request it no longer needs and save bandwidth, but the engine never depends
//   on it — the generation guard still discards stale results from a provider that ignores
//   abort. A provider that rejects *because* it was aborted (an `AbortError`) counts as a
//   cancellation (no rows), not a real error.
//
// `lastRows` — the keep-previous / no-flicker mechanism.
//   Holds the rows from the most recent successful result. By default, an async run keeps previous
//   rows only for providers that are still pending. A provider can instead supply the exact
//   presentation for its live query through `getPendingItems`; the engine publishes those rows
//   without interpreting them. Persistent surfaces can leave the entire prior settled state
//   untouched during loading, and retain successful rows after provider failures. A successful
//   zero-result query still emits `empty`. `cancel` clears retained rows.
//
// Sync fast-path: when every provider answers synchronously, publish in the same tick.
// Flat arrays and ordinary grouped results settle immediately; an explicit pending snapshot
// stays loading until the consumer publishes its next snapshot through live plugin props.
//
// Error isolation: rows merge per-provider, so one provider rejecting doesn't blank the
// others. An `error` state surfaces only when nothing resolved (zero rows and an error);
// zero rows with no error is `empty`.
//
// Run flow, step by step:
//   1. Abort the previous controller; bump `generation`; create a new controller.
//   2. Call each provider's `getResults(query, signal)`; tag each result sync or async.
//   3. All sync? → merge and `settle(generation, …)` immediately (explicit snapshots may be pending).
//   4. Any async? → emit `loading` with provider-owned pending rows or the matching `lastRows`,
//      then `Promise.all` the outcomes and `settle(generation, …)` once they resolve.
//   5. `settle` drops stale generations; otherwise emits `loading` for pending snapshots, `done` (rows),
//      `empty` (no rows, no error), or `error` (no rows, had an error), updating `lastRows`
//      on success.
//
// Mental model: `generation` decides whether a result is still wanted, `AbortController`
// tries to stop work that isn't, and provider pending rows / `lastRows` keep the UI stable while
// the wanted result loads. Each provider can configure its own debounce behavior; the engine
// schedules providers independently and always executes a bare-trigger query immediately.
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { DebouncedScheduler } from '#pplx/frontend/composer-sdk/internal/typeahead/DebouncedScheduler.js';
/**
 * Owns the fetch-results lifecycle for the active trigger: `run` fetches from every
 * provider, merges their rows, and emits a single state via `onStateChange`. See the file
 * header for how overlapping runs stay correct and flicker-free.
 */
export class TypeaheadResultsEngine {
    generation = 0;
    abortController = null;
    lastRows = [];
    // Snapshot whose indexes `lastRows.providerIndex` references.
    lastProviders = [];
    hasSettledResult = false;
    /** Last emitted state, republished immediately when continuation latches before live props update. */
    currentState = { status: 'idle' };
    /** Query whose provider highlight identities are retained; cancellation clears it. */
    highlightQuery = null;
    /** Registration ids survive live props; standalone providers fall back to object identity. */
    providerHighlights = new Map();
    providerSchedules = new Map();
    onStateChange;
    retainPreviousRowsDuringLoading;
    retainPreviousRowsOnError;
    /** Create an engine that reports each state change through `options.onStateChange`. */
    constructor(options) {
        this.onStateChange = options.onStateChange;
        this.retainPreviousRowsDuringLoading = options.retainPreviousRowsDuringLoading ?? false;
        this.retainPreviousRowsOnError = options.retainPreviousRowsOnError ?? false;
    }
    /** Abort any in-flight query and forget the kept-previous rows (trigger closed). */
    cancel() {
        this.providerSchedules.forEach(({ scheduler }) => scheduler.cancel());
        this.providerSchedules.clear();
        this.abortController?.abort();
        this.abortController = null;
        this.generation += 1;
        this.lastRows = [];
        this.lastProviders = [];
        this.hasSettledResult = false;
        this.currentState = { status: 'idle' };
        this.highlightQuery = null;
        this.providerHighlights.clear();
    }
    /** Fetch `query` from `providers`, superseding any in-flight run. */
    run(query, providers) {
        this.abortController?.abort();
        const generation = (this.generation += 1);
        const controller = new AbortController();
        this.abortController = controller;
        const providerSnapshot = [...providers];
        if (query !== this.highlightQuery)
            this.providerHighlights.clear();
        this.highlightQuery = query;
        const identities = new Set(providers.map((provider) => provider.registration?.id ?? provider));
        this.providerHighlights.forEach((_retention, identity) => {
            if (!identities.has(identity))
                this.providerHighlights.delete(identity);
        });
        this.reconcileProviderSchedules(providerSnapshot);
        const pendingRowsByProvider = providerSnapshot.map((provider, providerIndex) => this.getPendingRows(provider, providerIndex, query));
        // Normalize each provider to a settled outcome (sync) or a promise of one (async). A honored
        // abort counts as cancellation (no rows), never an error; stale generations drop in `settle`.
        const outcomes = providerSnapshot.map((provider, index) => {
            try {
                const results = this.runProvider(provider, query, controller.signal);
                if (!isThenable(results)) {
                    return this.resolveOutcome(results, provider, index, generation, controller.signal);
                }
                return results.then((items) => this.resolveOutcome(items, provider, index, generation, controller.signal), (error) => (isAbortError(error) ? { rows: [] } : { error }));
            }
            catch (error) {
                return { error };
            }
        });
        // All synchronous → settle now, skipping the loading flash (the static-data path).
        if (outcomes.every((outcome) => !isThenable(outcome))) {
            this.settle(generation, reduceOutcomes(outcomes), providerSnapshot);
            return;
        }
        // Persistent surfaces normally retain the last settled screen as-is. A provider supplying
        // pending rows owns the exact presentation for the live query, so publish those rows even on
        // a persistent surface while its authoritative result is still pending.
        const hasPendingRows = outcomes.some((outcome, providerIndex) => isThenable(outcome) && pendingRowsByProvider[providerIndex] !== undefined);
        const hasSnapshots = outcomes.some((outcome) => !isThenable(outcome) && 'snapshot' in outcome);
        if (!this.retainPreviousRowsDuringLoading ||
            !this.hasSettledResult ||
            hasPendingRows ||
            hasSnapshots) {
            const loadingOutcomes = outcomes.map((outcome, providerIndex) => {
                if (isThenable(outcome)) {
                    const provider = providerSnapshot[providerIndex];
                    if (!provider) {
                        return { rows: [] };
                    }
                    return {
                        rows: pendingRowsByProvider[providerIndex] ??
                            this.previousRowsForProvider(provider, providerIndex),
                    };
                }
                return outcome;
            });
            const merged = reduceOutcomes(loadingOutcomes);
            this.emit({ status: 'loading', rows: merged.rows, ...snapshotState(merged.snapshots) });
        }
        void Promise.all(outcomes.map((outcome) => Promise.resolve(outcome))).then((settled) => this.settle(generation, reduceOutcomes(settled), providerSnapshot));
    }
    resolveOutcome(results, provider, providerIndex, generation, signal) {
        const rows = toResolvedRows(results, provider, providerIndex);
        // A late result must not restore retention after another query or registration has won.
        if (signal.aborted || generation !== this.generation)
            return { rows };
        const result = 'groups' in results ? results : undefined;
        const identity = provider.registration?.id ?? provider;
        const previous = this.providerHighlights.get(identity);
        const continued = previous?.continued === true || result?.continuation !== undefined;
        let highlightKey;
        if (result?.status !== undefined || continued) {
            const retention = previous ?? { key: Symbol(), continued };
            retention.continued = continued;
            this.providerHighlights.set(identity, retention);
            highlightKey = retention.key;
        }
        if (result?.status === undefined &&
            result?.emptyMessage === undefined &&
            result?.continuation === undefined &&
            !continued) {
            return { rows };
        }
        const status = result?.status ?? 'settled';
        let emptyMessage = provider.emptyMessage;
        switch (status) {
            case 'pending':
                emptyMessage = result?.emptyMessage;
                break;
            case 'settled':
                emptyMessage = result?.emptyMessage ?? provider.emptyMessage;
                break;
            default:
                return assertUnreachableValue(status);
        }
        return {
            rows,
            snapshot: {
                providerIndex,
                sectionId: provider.section?.id ?? null,
                sectionTitle: provider.section?.title ?? null,
                status,
                hasRows: rows.length > 0,
                highlightKey,
                emptyMessage,
                continuation: result?.continuation === undefined
                    ? undefined
                    : this.guardContinuation(result.continuation, generation, signal),
            },
        };
    }
    guardContinuation(continuation, generation, signal) {
        let requested = false;
        return {
            get isPending() {
                // Async siblings may publish this snapshot again after a request has already started.
                return continuation.isPending || requested;
            },
            request: (reason) => {
                if (requested ||
                    continuation.isPending ||
                    signal.aborted ||
                    generation !== this.generation) {
                    return;
                }
                // A second click/scroll can arrive before the consumer's live props update. Latch this
                // snapshot immediately, then let the next consumer snapshot own the pending lifecycle.
                requested = true;
                const state = this.currentState;
                switch (state.status) {
                    case 'loading':
                    case 'done':
                    case 'empty':
                        this.emit({ ...state });
                        break;
                    case 'idle':
                    case 'error':
                        return;
                    default:
                        return assertUnreachableValue(state);
                }
                continuation.request(reason);
            },
        };
    }
    emit(state) {
        this.currentState = state;
        this.onStateChange(state);
    }
    runProvider(provider, query, signal) {
        const debounceConfig = provider.debounce;
        if (query.length === 0 || debounceConfig === undefined) {
            this.removeProviderSchedule(provider);
            return provider.getResults(query, signal);
        }
        return this.getProviderSchedule(provider, debounceConfig).run(query, signal);
    }
    getProviderSchedule(provider, debounceConfig) {
        const { waitMs, maxWaitMs, leading = false } = debounceConfig;
        const existing = this.providerSchedules.get(provider);
        if (existing?.waitMs === waitMs &&
            existing.maxWaitMs === maxWaitMs &&
            existing.leading === leading) {
            return existing.scheduler;
        }
        existing?.scheduler.cancel();
        const scheduler = new DebouncedScheduler({
            fn: (query, signal) => provider.getResults(query, signal),
            waitMs,
            maxWaitMs,
            leading,
        });
        this.providerSchedules.set(provider, { scheduler, waitMs, maxWaitMs, leading });
        return scheduler;
    }
    reconcileProviderSchedules(providers) {
        const activeProviders = new Set(providers);
        this.providerSchedules.forEach(({ scheduler }, provider) => {
            if (activeProviders.has(provider)) {
                return;
            }
            scheduler.cancel();
            this.providerSchedules.delete(provider);
        });
    }
    removeProviderSchedule(provider) {
        this.providerSchedules.get(provider)?.scheduler.cancel();
        this.providerSchedules.delete(provider);
    }
    getPendingRows(provider, providerIndex, query) {
        const items = provider.getPendingItems?.(query);
        return items === undefined ? undefined : toResolvedRows(items, provider, providerIndex);
    }
    settle(generation, merged, providers) {
        if (generation !== this.generation) {
            return; // superseded by a newer query
        }
        const snapshots = snapshotState(merged.snapshots);
        if (merged.snapshots.some((snapshot) => isPendingSnapshot(snapshot))) {
            this.emit({ status: 'loading', rows: merged.rows, ...snapshots });
            return;
        }
        this.hasSettledResult = true;
        if (merged.rows.length > 0) {
            this.lastRows = merged.rows;
            this.lastProviders = providers;
            this.emit({ status: 'done', rows: merged.rows, ...snapshots });
            return;
        }
        if (merged.error !== undefined && this.retainPreviousRowsOnError) {
            const retainedRows = providers.flatMap((provider, providerIndex) => this.previousRowsForProvider(provider, providerIndex));
            if (retainedRows.length > 0) {
                this.lastRows = retainedRows;
                this.lastProviders = providers;
                this.emit({ status: 'done', rows: retainedRows, ...snapshots });
                return;
            }
        }
        this.lastRows = [];
        this.lastProviders = [];
        this.emit(merged.error !== undefined && merged.snapshots.length === 0
            ? { status: 'error', error: merged.error }
            : { status: 'empty', ...snapshots });
    }
    previousRowsForProvider(provider, providerIndex) {
        const previousProviderIndex = this.lastProviders.indexOf(provider);
        if (previousProviderIndex === -1) {
            return [];
        }
        return this.lastRows
            .filter((row) => row.providerIndex === previousProviderIndex)
            .map((row) => ({ ...row, providerIndex }));
    }
}
function isPendingSnapshot(snapshot) {
    switch (snapshot.status) {
        case 'pending':
            return true;
        case 'settled':
            return false;
        default:
            return assertUnreachableValue(snapshot.status);
    }
}
function snapshotState(snapshots) {
    return snapshots.length > 0 ? { providerSnapshots: snapshots } : {};
}
function isThenable(value) {
    return typeof value?.then === 'function';
}
function isAbortError(error) {
    return error?.name === 'AbortError';
}
/**
 * Flattens one provider result set into selectable rows while retaining the provider, section, and
 * group metadata needed for selection and rendering.
 *
 * @example
 * ```ts
 * { groups: [{ id: 'modes', items: [search, research] }] }
 * // becomes:
 * [
 *   { item: search, groupId: 'modes', groupIndex: 0 },
 *   { item: research, groupId: 'modes', groupIndex: 0 },
 * ]
 * ```
 */
function toResolvedRows(results, provider, providerIndex) {
    const toResolvedRow = (item, group) => ({
        item,
        providerIndex,
        sectionId: provider.section?.id ?? null,
        sectionTitle: provider.section?.title ?? null,
        ...(group === undefined
            ? {}
            : {
                groupId: group.id,
                groupIndex: group.index,
                ...(group.title === undefined ? {} : { groupTitle: group.title }),
            }),
    });
    if ('groups' in results) {
        return results.groups.flatMap((group, groupIndex) => group.items.map((item) => toResolvedRow(item, { id: group.id, index: groupIndex, title: group.title })));
    }
    return results.map((item) => toResolvedRow(item));
}
// Merge per-provider outcomes in registration order: rows concatenate (preserving
// section order), and an error only "wins" when no provider produced any rows.
function reduceOutcomes(outcomes) {
    const rows = outcomes.flatMap((outcome) => ('rows' in outcome ? outcome.rows : []));
    const firstError = outcomes.find((outcome) => 'error' in outcome);
    const snapshots = outcomes.flatMap((outcome) => 'snapshot' in outcome && outcome.snapshot !== undefined ? [outcome.snapshot] : []);
    return { rows, snapshots, error: firstError?.error };
}
//# sourceMappingURL=TypeaheadResultsEngine.js.map